import React, { useState, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
} from "@mui/material";
import MenuIcon from "@material-ui/icons/Menu";
import "./DynamicTable.scss";

const MAX_COLUMNS = 10;

function DynamicTable({ data }) {
  const allKeys = data && data.length > 0 ? Object.keys(data[0]) : [];
  const [selectedCols, setSelectedCols] = useState(allKeys.slice(0, 5));
  const [anchorEl, setAnchorEl] = useState(null);

  const safeValue = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "object") return JSON.stringify(val);
    return String(val);
  };

  const columns = useMemo(() => {
    return selectedCols.map((key) => ({
      field: key,
      headerName: key,
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const value = safeValue(params.value);
        if (typeof value === "string") {
          if (value.toLowerCase() === "yes") {
            return <div className="yes-cell">Yes</div>;
          } else if (value.toLowerCase() === "no") {
            return <div className="no-cell">No</div>;
          }
        }
        if (key === "Email address") {
          const [local, domain] = value.split("@");
          return (
            <div className="email-cell">
              <div className="email-local" title={local}>
                {local}
              </div>
              {domain && <div className="email-domain">@{domain}</div>}
            </div>
          );
        }
        return (
          <div className="default-cell" title={value}>
            {value}
          </div>
        );
      },
    }));
  }, [selectedCols]);

  const rows = useMemo(() => {
    return data.map((row, index) => {
      const formattedRow = {};
      for (const key in row) {
        formattedRow[key] = safeValue(row[key]);
      }
      return { id: index, ...formattedRow };
    });
  }, [data]);

  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  // Handlers
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleColumnToggle = (key) => {
    if (selectedCols.includes(key)) {
      setSelectedCols(selectedCols.filter((c) => c !== key));
    } else if (selectedCols.length < MAX_COLUMNS) {
      setSelectedCols([...selectedCols, key]);
    }
  };

  return (
    <div className="dynamic-table-parent-container">
      <div className="dynamic-table-container">
        <div className="icon-container">
          <IconButton
            onClick={handleMenuOpen}
            className={anchorEl ? "icon-container-tip anchor" : "icon-container-tip"}
          >
            <MenuIcon />
          </IconButton>

          {/* Optional: small tip arrow */}
          {anchorEl && (
            <div
              className="anchor-container"
            />
          )}
        </div>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            style: {
              maxHeight: 400, // reduced height
              width: 500,
            },
          }}
        >
          <MenuItem disabled>
            <strong>Select Columns</strong>
          </MenuItem>
          <Divider />
          {allKeys.map((key) => (
            <MenuItem
              key={key}
              onClick={() => handleColumnToggle(key)}
              disabled={
                selectedCols.length >= MAX_COLUMNS &&
                !selectedCols.includes(key)
              }
            >
              <Checkbox checked={selectedCols.includes(key)} />
              <ListItemText primary={key} />
            </MenuItem>
          ))}
        </Menu>

        <span>Data View</span>
      </div>

      {/* DataGrid */}
      <div className="dynamic-table">
        <DataGrid
          rows={rows}
          columns={columns}
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          hideFooter
          autoHeight={false}
          sx={{
            height: Math.min(rows.length * 58 + 53, 500), // 58px per row + 68px header
            "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal !important",
              lineHeight: "normal !important",
            },
            border: "1px solid #ddd",
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: "1px solid #ccc",
            },
            "& .MuiDataGrid-cell": {
              borderRight: "1px solid #eee",
            },
          }}
        />
      </div>
    </div>
  );
}

export default DynamicTable;
